/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-12 10:27:50
 *
 *******************************************************/
//@flow
import moment from 'moment';
import React, { Component } from 'react';
import {
  Button,
  Popover,
  Select,
  Stack,
  FormLayout,
  DatePicker,
  TextField
} from '@shopify/polaris';
import { SelectMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { buildDateFromDateRange, buildDateRangeFromDate } from 'src/helpers/product-ranking-helper';
import { getStoreDate, displayDateRange } from 'src/helpers/date-time-helper';
import { DATE_RANGE } from 'src/constants/product-ranking';
import { DATE_FORMAT } from 'src/constants/language-setting';

type Props = WithI18nProps;

type State = {
  popoverActive: boolean,
  selectedDateRange: string,
  startDate: string,
  endDate: string,
  defaultStartDate: Date | string,
  defaultEndDate: Date | string,
  month: number,
  year: number,
  selectedDates: {
    start: Date,
    end: Date
  }
};
// DONE
class RenderDateRangePickerComponent extends Component<Props, State> {
  today = getStoreDate(new Date());
  state = {
    popoverActive: false,
    // Dropdown date value
    selectedDateRange: 'none',
    // Date changed from input
    startDate: '',
    endDate: '',
    // Date passed from form
    defaultStartDate: '',
    defaultEndDate: '',

    // Date picker
    month: this.today.getMonth(),
    year: this.today.getFullYear(),
    selectedDates: {
      start: this.today,
      end: this.today
    }
  };

  componentDidMount() {
    const { enabledFromDate, enabledToDate } = this.props;
    this.initialValue(enabledFromDate, enabledToDate);
  }

  componentWillUpdate(nextProps, nextState) {
    const { enabledFromDate, enabledToDate } = nextProps;
    const { enabledFromDate: currentEnabledFromDate, enabledToDate: currentEnabledToDate } =
      this.props;

    if (currentEnabledFromDate === enabledFromDate && currentEnabledToDate === enabledToDate) {
      return;
    }

    this.initialValue(enabledFromDate, enabledToDate);
  }

  render() {
    const {
      popoverActive,
      selectedDateRange,
      month,
      year,
      selectedDates,
      startDate,
      endDate,
      defaultStartDate,
      defaultEndDate
    } = this.state;

    const { disabled, i18n } = this.props;

    const activator = (
      <div className="date-select">
        <Button
          disabled={disabled}
          onClick={this.togglePopoverActive}
          icon={SelectMinor}
          fullWidth
          textAlign="left">
          {!defaultStartDate && !defaultEndDate
            ? i18n.translate('AdminHTML.ProductRanking.Constant.dateRange1_Label')
            : displayDateRange(defaultStartDate, defaultEndDate)}
        </Button>
      </div>
    );

    return (
      <div className="datePicker">
        <Popover active={popoverActive} activator={activator} onClose={this.closePopover} sectioned>
          <Popover.Pane>
            <Popover.Section>
              <FormLayout>
                <Select
                  label={i18n.translate(
                    'AdminHTML.ProductRanking.RenderDateRangePickerComponent.select_Label'
                  )}
                  options={DATE_RANGE.map(function (value) {
                    return {
                      ...value,
                      label: i18n.translate(value.label)
                    };
                  })}
                  onChange={this.handleSelectChange}
                  value={selectedDateRange}
                />
                <FormLayout.Group>
                  <TextField
                    label={i18n.translate(
                      'AdminHTML.ProductRanking.RenderDateRangePickerComponent.field1_Label'
                    )}
                    value={startDate}
                    onChange={this.handleChangeStarting}
                    onBlur={this.handleBlurStarting}
                    disabled={selectedDateRange === 'none'}
                  />
                  <TextField
                    label={i18n.translate(
                      'AdminHTML.ProductRanking.RenderDateRangePickerComponent.field2_Label'
                    )}
                    value={endDate}
                    onChange={this.handleChangeEnding}
                    onBlur={this.handleBlurEnding}
                    disabled={selectedDateRange === 'none'}
                  />
                </FormLayout.Group>
                <DatePicker
                  month={month}
                  year={year}
                  onChange={this.setSelectedDates}
                  onMonthChange={this.handleMonthChange}
                  selected={selectedDates}
                  multiMonth={true}
                  allowRange={true}
                  disabled={selectedDateRange === 'none'}
                />
              </FormLayout>
            </Popover.Section>
          </Popover.Pane>
          <Popover.Pane fixed>
            <Popover.Section>
              <div className="BC-Popover__Button">
                <Stack>
                  <Button onClick={this.closePopover}>
                    {i18n.translate('AdminHTML.Common.Action.cancel')}
                  </Button>
                  <Button primary onClick={this.handleApply}>
                    {i18n.translate('AdminHTML.Common.Action.apply')}
                  </Button>
                </Stack>
              </div>
            </Popover.Section>
          </Popover.Pane>
        </Popover>
      </div>
    );
  }

  // Toggle state to show/hide the popover
  togglePopoverActive = () => {
    this.setState(({ popoverActive }) => {
      return { popoverActive: !popoverActive };
    });
  };

  // initial value for date in popover
  initialValue = (enabledFromDate, enabledToDate) => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;

    // Initial value
    const today = getStoreDate(new Date());
    if (
      (enabledFromDate === undefined || !moment(enabledFromDate).isValid()) &&
      (enabledToDate === undefined || !moment(enabledToDate).isValid())
    ) {
      // the from date and to date are not valid, set today is start/end date
      const selectedDateRange = 'none';
      const startDate = '';
      const endDate = '';
      const selectedDates = {
        start: today,
        end: today
      };

      this.setState({
        selectedDateRange,
        startDate,
        endDate,
        defaultStartDate: undefined,
        defaultEndDate: undefined,
        selectedDates
      });
    } else {
      // one of or both from date and to date are valid
      let startDate = '';
      let endDate = '';
      let selectedDates = {};

      if (enabledFromDate === undefined || !moment(enabledFromDate).isValid()) {
        // from date is not valid, set start date is today/to date
        const today = getStoreDate(new Date());
        if (moment(enabledToDate).isAfter(today)) {
          startDate = moment(today).format(formatDateNumber);
          selectedDates.start = today;
        } else {
          startDate = moment(enabledToDate).format(formatDateNumber);
          selectedDates.start = moment(enabledToDate).toDate();
        }
      } else {
        // from date is valid, set start date is from date
        startDate = moment(enabledFromDate).format(formatDateNumber);
        selectedDates.start = moment(enabledFromDate).toDate();
      }

      if (enabledToDate === undefined || !moment(enabledToDate).isValid()) {
        // to date is not valid, set end date is today/from date
        const today = getStoreDate(new Date());
        if (moment(enabledFromDate).isAfter(today)) {
          endDate = moment(enabledFromDate).format(formatDateNumber);
          selectedDates.end = moment(enabledFromDate).toDate();
        } else {
          endDate = moment(today).format(formatDateNumber);
          selectedDates.end = today;
        }
      } else {
        // to date is valid, set end date is to date
        endDate = moment(enabledToDate).format(formatDateNumber);
        selectedDates.end = moment(enabledToDate).toDate();
      }

      this.setState({
        selectedDateRange: buildDateRangeFromDate(selectedDates.start, selectedDates.end),
        startDate,
        endDate,
        defaultStartDate: selectedDates.start,
        defaultEndDate: selectedDates.end,
        selectedDates
      });
    }
  };

  // Hanlde cancle/close popover
  closePopover = () => {
    const { defaultStartDate, defaultEndDate } = this.state;

    //reset value
    this.initialValue(defaultStartDate, defaultEndDate);

    this.togglePopoverActive();
  };

  // Handle set range select
  handleSelectChange = selectedDateRange => {
    const dataState = buildDateFromDateRange(selectedDateRange);

    this.setState({ ...dataState, selectedDateRange });
  };

  // Handle change custom date
  setSelectedDates = selectedDates => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const startDate = moment(selectedDates.start).format(formatDateNumber);
    const endDate = moment(selectedDates.end).format(formatDateNumber);
    const selectedDateRange = buildDateRangeFromDate(selectedDates.start, selectedDates.end);

    this.setState({ selectedDates, startDate, endDate, selectedDateRange });
  };

  // Handle change month
  handleMonthChange = (month, year) => {
    this.setState({ month, year });
  };

  // Handle start date input data
  handleChangeStarting = value => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;

    let { selectedDates } = this.state;
    selectedDates.start = moment(value, formatDateNumber).isValid()
      ? moment(value, formatDateNumber).toDate()
      : getStoreDate(new Date());

    this.setState({ startDate: value, selectedDates });
  };

  // Handle blur start date input data
  handleBlurStarting = () => {
    // check if the start date is not valid, change to today/end date
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const {
      startDate,
      selectedDates: { start, end }
    } = this.state;

    if (!moment(startDate, formatDateNumber).isValid()) {
      const today = getStoreDate(getStoreDate(new Date()));
      if (moment(end).isAfter(today)) {
        this.handleChangeStarting(moment(today).format(formatDateNumber));
      } else {
        this.handleChangeStarting(moment(end).format(formatDateNumber));
      }
    } else {
      if (moment(start).isAfter(moment(end))) {
        this.handleChangeStarting(moment(end).format(formatDateNumber));
      }
    }

    // Update seletedDateRange
    const selectedDateRange = buildDateRangeFromDate(start, end);
    this.setState({ selectedDateRange });
  };

  // Handle end date input data
  handleChangeEnding = value => {
    const { appLanguage } = this.props;
    let { selectedDates } = this.state;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    selectedDates.end = moment(value, formatDateNumber).isValid()
      ? moment(value, formatDateNumber).toDate()
      : getStoreDate(new Date());

    this.setState({ endDate: value, selectedDates });
  };

  // Handle blur end date input data
  handleBlurEnding = () => {
    // check if the end date is not valid, change to today/start date
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const {
      endDate,
      selectedDates: { start, end }
    } = this.state;

    if (!moment(endDate, formatDateNumber).isValid()) {
      const today = getStoreDate(new Date());
      if (moment(start).isAfter(today)) {
        this.handleChangeEnding(moment(start).format(formatDateNumber));
      } else {
        this.handleChangeEnding(moment(today).format(formatDateNumber));
      }
    } else {
      if (moment(start).isAfter(moment(end))) {
        this.handleChangeEnding(moment(start).format(formatDateNumber));
      }
    }

    // Update seletedDateRange
    const selectedDateRange = buildDateRangeFromDate(start, end);
    this.setState({ selectedDateRange });
  };

  // Handle apply button
  handleApply = () => {
    const { onChooseDate, index } = this.props;
    const { selectedDates, selectedDateRange } = this.state;

    onChooseDate(index, selectedDates, selectedDateRange);
    this.togglePopoverActive();
  };
}

export const RenderDateRangePicker = withI18n()(RenderDateRangePickerComponent);
